
import { useState, useRef, useEffect } from 'react';
import { getGoogleCalendarUrl, downloadICSFile, buildCalendarEvent } from '../../utils/calendar';
import { eventDetails } from '../../mocks/eventDetails';

interface AddToCalendarButtonProps {
  event: {
    id: number;
    title: string;
    month: string;
    day: string;
    location: string;
    time: string;
  };
  variant?: 'primary' | 'compact' | 'icon';
}

export default function AddToCalendarButton({ event, variant = 'primary' }: AddToCalendarButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const details = eventDetails[event.id];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (!details) return null;

  const calendarEvent = buildCalendarEvent(event, details);

  const handleGoogleCalendar = () => {
    window.open(getGoogleCalendarUrl(calendarEvent), '_blank', 'noopener,noreferrer');
    setIsOpen(false);
    setShowConfirm(true);
    setTimeout(() => setShowConfirm(false), 3000);
  };

  const handleAppleCalendar = () => {
    downloadICSFile(calendarEvent);
    setIsOpen(false);
    setShowConfirm(true);
    setTimeout(() => setShowConfirm(false), 3000);
  };

  const handleOutlook = () => {
    downloadICSFile(calendarEvent);
    setIsOpen(false);
    setShowConfirm(true);
    setTimeout(() => setShowConfirm(false), 3000);
  };

  if (variant === 'icon') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
          className="w-9 h-9 flex items-center justify-center bg-white/90 hover:bg-white rounded-full shadow-md transition-all cursor-pointer"
          title="Add to Calendar"
        >
          <i className="ri-calendar-todo-line text-teal-600 text-lg"></i>
        </button>

        {isOpen && (
          <div className="absolute right-0 top-11 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-52 z-50 animate-fade-in">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add to Calendar</p>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGoogleCalendar(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-google-fill text-lg text-red-500"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Google Calendar</span>
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAppleCalendar(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-apple-fill text-lg text-gray-800"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Apple Calendar</span>
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOutlook(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-mail-send-line text-lg text-sky-600"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Outlook / Other</span>
            </button>
          </div>
        )}

        {showConfirm && (
          <div className="absolute right-0 top-11 bg-teal-600 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap z-50 animate-fade-in">
            <i className="ri-check-line mr-1"></i> Event added!
          </div>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
          className="flex items-center space-x-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-todo-line text-sm"></i>
          <span>Add to Calendar</span>
        </button>

        {isOpen && (
          <div className="absolute left-0 top-9 bg-white rounded-xl shadow-xl border border-gray-100 py-2 w-52 z-50 animate-fade-in">
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add to Calendar</p>
            </div>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleGoogleCalendar(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-google-fill text-lg text-red-500"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Google Calendar</span>
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleAppleCalendar(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-apple-fill text-lg text-gray-800"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Apple Calendar</span>
            </button>
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleOutlook(); }}
              className="w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-teal-50 transition-colors cursor-pointer"
            >
              <div className="w-6 h-6 flex items-center justify-center">
                <i className="ri-mail-send-line text-lg text-sky-600"></i>
              </div>
              <span className="text-sm font-medium text-gray-700">Outlook / Other</span>
            </button>
          </div>
        )}

        {showConfirm && (
          <div className="absolute left-0 top-9 bg-teal-600 text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg whitespace-nowrap z-50 animate-fade-in">
            <i className="ri-check-line mr-1"></i> Event added!
          </div>
        )}
      </div>
    );
  }

  // Primary variant (full button)
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-center space-x-2 bg-amber-500 hover:bg-amber-600 text-white py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-calendar-todo-line text-lg"></i>
        <span>Add to Calendar</span>
        <i className={`ri-arrow-down-s-line text-lg transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-14 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 animate-fade-in">
          <button
            onClick={handleGoogleCalendar}
            className="w-full flex items-center space-x-3 px-5 py-3 hover:bg-teal-50 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-red-50 rounded-lg">
              <i className="ri-google-fill text-xl text-red-500"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Google Calendar</p>
              <p className="text-xs text-gray-500">Opens in a new tab</p>
            </div>
          </button>
          <button
            onClick={handleAppleCalendar}
            className="w-full flex items-center space-x-3 px-5 py-3 hover:bg-teal-50 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg">
              <i className="ri-apple-fill text-xl text-gray-800"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Apple Calendar</p>
              <p className="text-xs text-gray-500">Downloads .ics file</p>
            </div>
          </button>
          <button
            onClick={handleOutlook}
            className="w-full flex items-center space-x-3 px-5 py-3 hover:bg-teal-50 transition-colors cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-sky-50 rounded-lg">
              <i className="ri-mail-send-line text-xl text-sky-600"></i>
            </div>
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-900">Outlook / Other</p>
              <p className="text-xs text-gray-500">Downloads .ics file</p>
            </div>
          </button>
        </div>
      )}

      {showConfirm && (
        <div className="absolute left-0 right-0 top-14 bg-teal-600 text-white text-sm font-medium px-4 py-3 rounded-lg shadow-lg z-50 text-center animate-fade-in">
          <i className="ri-check-line mr-1"></i> Event added to your calendar!
        </div>
      )}
    </div>
  );
}
